<script setup lang="ts">
import { computed } from "vue";
import { displayFileName } from "../../lib/fileNames";
import {
  entityChipAccentStyle,
  entityEffectiveColor,
} from "../../lib/entityColors";
import { just, unwrapMaybeOr, toMaybe, isJust } from "../../lib/maybe";
import type { DatabaseEntrySummary } from "../../lib/outlineEntityTypes";
import type { TimelineFilters } from "../../lib/timelineFilters";
import ZwSearchableMenu from "../ui/ZwSearchableMenu.vue";

const props = defineProps<{
  filters: TimelineFilters;
  entries: DatabaseEntrySummary[];
}>();
const emit = defineEmits<{ change: [filters: TimelineFilters] }>();
const options = computed(() =>
  props.entries
    .filter(
      (entry) =>
        entry.isMap === false &&
        props.filters.entries.includes(entry.name) === false,
    )
    .map((entry) => ({
      value: entry.name,
      label: `${displayFileName(entry.name)} · ${unwrapMaybeOr(entry.fields.type, "Other")}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);
const selected = computed(() =>
  props.filters.entries.map((name) => {
    const entry = toMaybe(props.entries.find((entry) => entry.name === name));
    return {
      name,
      label: displayFileName(name),
      style: isJust(entry)
        ? unwrapMaybeOr(
            entityChipAccentStyle(
              entityEffectiveColor(
                entry.value.fields.color,
                entry.value.fields.type,
              ),
            ),
            {},
          )
        : {},
    };
  }),
);
function add(name: string): void {
  if (props.filters.entries.includes(name)) return;
  emit("change", {
    ...props.filters,
    entries: [...props.filters.entries, name],
  });
}
function remove(name: string): void {
  emit("change", {
    ...props.filters,
    entries: props.filters.entries.filter((entry) => entry !== name),
  });
}
</script>

<template>
  <section class="timeline-filters" aria-label="Timeline filters">
    <div class="timeline-filter-controls">
      <ul
        v-if="selected.length > 0"
        class="timeline-filter-chips"
        aria-label="Selected filters"
      >
        <li v-for="entry in selected" :key="entry.name" :style="entry.style">
          <span>{{ entry.label }}</span>
          <button
            type="button"
            :aria-label="`Remove ${entry.label} filter`"
            @click="remove(entry.name)"
          >
            ×
          </button>
        </li>
      </ul>
      <ZwSearchableMenu
        align="end"
        :options="options"
        :menu-label="just('Filter by worldbuilding entry')"
        search-placeholder="Search characters, locations…"
        empty-label="No more entries"
        @select="add"
      >
        <template #trigger="{ toggle, open }">
          <button
            type="button"
            class="timeline-filter-action"
            :aria-expanded="open"
            @click="toggle"
          >
            + Filter
          </button>
        </template>
      </ZwSearchableMenu>
    </div>
  </section>
</template>

<style scoped>
.timeline-filters {
  width: 100%;
  min-width: 0;
}
.timeline-filter-controls {
  min-height: 2.25rem;
  justify-content: flex-end;
}
.timeline-filter-controls,
.timeline-filter-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-sm);
}
.timeline-filter-controls > :deep(.zw-searchable-menu) {
  width: auto;
}
.timeline-filter-action {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: 0.8125rem;
  padding: var(--space-xs) 0;
  cursor: pointer;
}
.timeline-filter-action:hover {
  color: var(--text-strong);
}
.timeline-filter-chips {
  margin: 0;
  padding: 0;
  list-style: none;
  justify-content: flex-end;
  min-width: 0;
}
.timeline-filter-chips li {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  max-width: 100%;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 0 var(--space-sm);
  font-family: var(--font-prose);
  font-size: 0.8125rem;
}
.timeline-filter-chips span {
  overflow-wrap: anywhere;
}
.timeline-filter-chips button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: var(--space-xs);
}
button:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}
@media (pointer: coarse) {
  .timeline-filter-controls {
    min-height: calc(44px + 2px);
  }
  button {
    min-height: 44px;
  }
  .timeline-filter-chips button {
    min-width: 44px;
  }
}
</style>
